'use strict';
import has from 'lodash/has';
import isFunction from 'lodash/isFunction';
import isNumber from 'lodash/isNumber';
import noop from 'lodash/noop';
import diff from 'object-diff';
import * as T from 'prop-types';
import React, {PureComponent} from 'react';
import 'rxjs/add/observable/combineLatest';
import 'rxjs/add/observable/empty';
import 'rxjs/add/observable/merge';
import 'rxjs/add/observable/of';
import 'rxjs/add/observable/timer';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/delay';
import 'rxjs/add/operator/distinctUntilChanged';
import 'rxjs/add/operator/filter';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/mapTo';
import 'rxjs/add/operator/mergeAll';
import 'rxjs/add/operator/switchMap';
import {Observable} from 'rxjs/Observable';
import {ReplaySubject} from 'rxjs/ReplaySubject';
import {contextTypes} from '../../../_libs/redux-fusion';
import {ViewMode} from '../../../core-utilities/enums/ViewMode';
import {getMessagesFromProps} from '../../../core-utilities/react-i18n';
import {shouldStateUpdate} from '../../../core-utilities/react-utils';
import {epicErrorHandler} from '../../../core-utilities/rx-utils';
import {actions as chatChannelModalActions} from '../../redux/chatChannelModal';
import {logger} from '../logger';
import {CommentIndicatorView} from './CommentIndicatorView';


// CODE_TAG[Firebase] Binds to multiple refs in Firebase
export class CommentIndicatorComponent extends PureComponent {
  static propTypes = {
    className: T.string,
    titleClassName: T.string,
    title: T.string,
    displayTitle: T.bool,
    allowNav: T.bool,
    userId: T.string.isRequired,
    chatId: T.string,
    collection: T.string.isRequired,
    onHasUnread: T.func,
    onAccessError: T.func,
    onClick: T.func,

    init: T.bool,
  };

  static defaultProps = {
    init: true,
  };

  static contextTypes = contextTypes;

  static PULSE_DURATION = 1000;

  static messages = CommentIndicatorView.messages;
  static messageProps = CommentIndicatorView.messageProps;

  static defaultState = () => ({
    mode: ViewMode.LOADING,
    hasComments: false,
    hasUnread: false,
    pulseUnread: false,
  });

  static $inject = [
    'ConversationDomain',
    'UserDomain',
  ];

  constructor(props, context) {
    super(props, context);
    this.dispatch = context.store.dispatch;
    this.state = CommentIndicatorComponent.defaultState();
    this.handleShowChat = this.handleShowChat.bind(this);
  }

  handleShowChat(e) {
    e.stopPropagation();
    if (isFunction(this.props.onClick)) {
      this.props.onClick(e);
    } else {
      this.dispatch(chatChannelModalActions.showModal({
        target: {id: this.props.chatId, collection: this.props.collection},
        allowNav: this.props.allowNav,
      }));
    }
  }

  componentWillReceiveProps(nextProps) {
    if (this.props$ && !propsAreEqual(this.props, nextProps)) {
      this.props$.next(nextProps);
    }
  }

  render() {
    return (
      <CommentIndicatorView
        onClick={this.handleShowChat}
        className={this.props.className}
        titleClassName={this.props.titleClassName}
        title={this.props.title}
        displayTitle={this.props.displayTitle}
        {...this.state}
        {...getMessagesFromProps(this.props, CommentIndicatorView.messageProps)}
      />
    );
  }

  componentDidMount() {
    // Does not support lazy loading. init only used to disable container behavior in storybook
    if (this.props.init) {
      this.initData();
    }
  }

  initData() {
    const ConversationDomain = CommentIndicatorComponent.dependencies.ConversationDomain;
    this.props$ = new ReplaySubject(1);

    this.updateSubscription = this.props$
      .map(props => props.chatId)
      .filter(id => !!id)
      .distinctUntilChanged()
      .switchMap(id => {
        this.triedToLink = false;
        return ConversationDomain.checkName(id).catch(onErrorNull);
      })
      .subscribe(
        () => null,
        err => logger.error({err}, 'PANIC: props$ cancelled'),
        () => logger.error('PANIC: props$ cancelled'),
      );

    const state$ = this.props$
      .distinctUntilChanged(propsAreEqual)
      .switchMap(props =>
        !props.userId || !props.chatId
          ? Observable.of(CommentIndicatorComponent.defaultState())
          : Observable
            .combineLatest(
              CommentIndicatorComponent._buildCommentCount$(props.chatId),
              CommentIndicatorComponent._buildReadCount$(props.userId, props.chatId),
              (commentCount, readCount) =>
                CommentIndicatorComponent.updateComments(commentCount, readCount, this.props, this.state)
            )
            .catch((error, source$) => {
              // Try to link the conversation target once then give up
              if (!this.triedToLink && error && error.statusCode === 403) {
                this.triedToLink = true;
                return Observable.fromPromise(ConversationDomain.getLinked(props.chatId, props.collection))
                  .mergeMap(() => source$);
              } else {
                if (isFunction(this.props.onAccessError)) {
                  this.props.onAccessError(this.props.chatId);
                }
                CommentIndicatorComponent.removeUserComments(props.userId, props.chatId);
                return Observable.empty();
              }
            })
            .mergeAll()
      )
      .catch(epicErrorHandler(logger, 'commentCount$ + readCount$'));

    this.stateSubscription = state$
      .subscribe(
        nextState => {
          if (shouldStateUpdate(this.state, nextState)) {
            this.setState(nextState);
          }
        },
        err => logger.error({err}, 'PANIC: commentCount$ + readCount$ cancelled'),
        () => logger.error('PANIC: commentCount$ + readCount$ cancelled'),
      );

    // publish initial value after observables are wired up
    this.props$.next(this.props);
  }

  componentWillUnmount() {
    this.props$ = null;
    // cleanup calendar data observable
    this.updateSubscription && this.updateSubscription.unsubscribe();
    this.stateSubscription && this.stateSubscription.unsubscribe();
  }

  static _buildCommentCount$(id) {
    return CommentIndicatorComponent.dependencies.ConversationDomain
      .getCommentCountRef(id)
      .observe('value')
      .map(coerceToNumber);
  }

  static _buildReadCount$(userId, id) {
    return CommentIndicatorComponent.dependencies.UserDomain
      .getCommentsRef(userId, id)
      .observe('value')
      .map(coerceToNumber);
  }

  static updateComments(commentCount, readCount, props, currentState) {
    const delta = commentCount - readCount;
    const nextState = diff(currentState, {
      mode: ViewMode.SUCCESS,
      hasComments: commentCount > 0,
      hasUnread: delta > 0,
    });

    if (has(nextState, 'hasUnread') && isFunction(props.onHasUnread))
      props.onHasUnread(props.chatId, nextState.hasUnread);

    if (delta > 0 && !currentState.pulseUnread) {
      nextState.pulseUnread = true;
      return Observable.merge(
        Observable.of(nextState),
        Observable.timer(CommentIndicatorComponent.PULSE_DURATION).mapTo({pulseUnread: false}),
      );
    } else {
      return Object.keys(nextState).length > 0 ? Observable.of(nextState) : Observable.empty();
    }
  }

  static removeUserComments(userId, id) {
    // cleanup user metrics
    return CommentIndicatorComponent.dependencies.UserDomain.removeCommentsRef(userId, id).then(noop, err => {
      logger.error({err}, 'CommentIndicatorDirective.commentsObservers error occurred in comment count observer');
    });
  }
}

function propsAreEqual(a, b) {
  if (!a && !b) {
    return true;
  } else if (!a || !b) {
    return false;
  } else {
    return (
      a.userId === b.userId
      && a.chatId === b.chatId
      && a.init === b.init
    );
  }
}

function coerceToNumber(ss) {
  const value = ss.exists() ? ss.val() : 0;
  return isNumber(value) ? value : 0;
}

function onErrorNull() {
  return null;
}



// WEBPACK FOOTER //
// ./src/js/comments/components/CommentIndicator/CommentIndicatorComponent.js